import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Truck, DollarSign, Package, Building2, Percent, Receipt, FileText, Clock, CreditCard, Printer, CheckCircle, XCircle, ShoppingCart, User } from 'lucide-react';
import { getPembelian, getPembelianById, createPembelian, updatePembelian, deletePembelian, getInventoriSpareparts, getGudang, getSupplierDebt, getSupplier, verifikasiPembelian, getPurchaseOrders, getPurchaseOrderById } from '../../api';
import SearchableSelect from '../../components/SearchableSelect';
import { useAuth } from '../../AuthContext';

// Helper: rentang tanggal awal & akhir bulan berjalan (YYYY-MM-DD, dihitung lokal agar tidak kena bug timezone UTC)
const getCurrentMonthRange = () => {
  const now = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    awal: fmt(new Date(now.getFullYear(), now.getMonth(), 1)),
    akhir: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};

export default function Pembelian() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [gudangList, setGudangList] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter tanggal — default bulan berjalan (bisa dikosongkan untuk menampilkan semua)
  const [filterTglAwal, setFilterTglAwal] = useState(getCurrentMonthRange().awal);
  const [filterTglAkhir, setFilterTglAkhir] = useState(getCurrentMonthRange().akhir);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [supplierDebts, setSupplierDebts] = useState([]);
  const [selectedSupplierInfo, setSelectedSupplierInfo] = useState(null);
  const [diskonMode, setDiskonMode] = useState('persen'); // 'persen' or 'rupiah'
  const [poList, setPoList] = useState([]);
  const [showPoPicker, setShowPoPicker] = useState(false);

  const [form, setForm] = useState({
    tanggal: new Date().toISOString().slice(0, 10), supplier_name: '', supplier_phone: '',
    supplier_address: '', gudang_id: '', notes: '',
    top: 0, metode_pembayaran: '',
    diskon_persen: 0, diskon_rupiah: 0,
    ppn_aktif: false, ppn_persen: 11,
    biaya_lain: 0, biaya_lain_catatan: '',
    no_faktur: '', nama_petugas: '',
    items: [{ sparepart_id: '', qty: 1, harga_satuan: 0, keterangan: '' }]
  });

  const load = () => {
    setLoading(true);
    Promise.all([getPembelian(), getInventoriSpareparts({}), getGudang(), getSupplierDebt(), getSupplier()])
      .then(([res, spRes, gRes, debtRes, supRes]) => {
        setData(res.data);
        setSpareparts(spRes.data || []);
        setGudangList(gRes.data || []);
        setSupplierDebts(debtRes.data || []);
        setSuppliers(supRes.data || []);
      })
      .catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh gudang, supplier & debt list when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        getGudang().then(res => setGudangList(res.data || [])).catch(() => {});
        getSupplier().then(res => setSuppliers(res.data || [])).catch(() => {});
        getSupplierDebt().then(res => setSupplierDebts(res.data || [])).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

  // Data tampilan sesuai filter tanggal (kosong = semua periode)
  const filteredData = data.filter(d =>
    (!filterTglAwal || !filterTglAkhir || ((d.tanggal || '') >= filterTglAwal && (d.tanggal || '') <= filterTglAkhir))
  );
  const diskonRupiah = useCallback(() => {
    const subtotalItems = form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.harga_satuan) || 0), 0);
    if (diskonMode === 'persen' && form.diskon_persen > 0) {
      return subtotalItems * (form.diskon_persen / 100);
    }
    return Number(form.diskon_rupiah) || 0;
  }, [form.items, form.diskon_persen, form.diskon_rupiah, diskonMode]);

  const totalQty = form.items.reduce((s, i) => s + (Number(i.qty) || 0), 0);
  const subtotalItems = form.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.harga_satuan) || 0), 0);
  const diskonValue = diskonRupiah();
  const dpp = subtotalItems - diskonValue;
  const ppn = form.ppn_aktif ? dpp * ((form.ppn_persen || 11) / 100) : 0;
  const biayaLain = Number(form.biaya_lain) || 0;
  const grandTotal = dpp + ppn + biayaLain;

  // Load supplier debt on modal open
  useEffect(() => {
    if (showModal) {
      getSupplierDebt()
        .then(res => setSupplierDebts(res.data || []))
        .catch(() => {});
    }
  }, [showModal]);

  // Handle supplier selection from master data
  // Load available PO list when opening modal
  useEffect(() => {
    if (showPoPicker) {
      getPurchaseOrders()
        .then(res => setPoList(res.data || []))
        .catch(() => {});
    }
  }, [showPoPicker]);

  // Tarik data dari Purchase Order
  const handleTarikPO = async (poId) => {
    if (!poId) return;
    try {
      const res = await getPurchaseOrderById(poId);
      const po = res.data;
      if (!po) { alert('Gagal memuat data PO'); return; }

      setForm(f => ({
        ...f,
        supplier_name: po.supplier_name || '',
        supplier_phone: po.supplier_phone || '',
        supplier_address: po.supplier_address || '',
        gudang_id: po.gudang_id || '',
        notes: po.notes ? `Dari PO: ${po.no_po}\n${po.notes}` : `Dari PO: ${po.no_po}`,
        no_faktur: '',
        items: (po.items || []).map(it => ({
          sparepart_id: it.sparepart_id,
          qty: it.qty,
          harga_satuan: it.harga_satuan,
          keterangan: it.keterangan || ''
        }))
      }));

      setShowPoPicker(false);

      // Cek hutang supplier
      const debt = supplierDebts.find(d => d.supplier_name.toLowerCase() === (po.supplier_name || '').toLowerCase());
      setSelectedSupplierInfo(debt || null);
    } catch (err) {
      alert('Gagal menarik data dari PO: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSupplierSelect = (supId) => {
    if (!supId) {
      setForm(f => ({ ...f, supplier_name: '', supplier_phone: '', supplier_address: '' }));
      setSelectedSupplierInfo(null);
      return;
    }
    const sup = suppliers.find(s => s.id === parseInt(supId));
    if (sup) {
      setForm(f => ({ ...f, supplier_name: sup.nama, supplier_phone: sup.telepon || '', supplier_address: sup.alamat || '' }));
      // Lookup debt
      const debt = supplierDebts.find(d => d.supplier_name.toLowerCase() === sup.nama.toLowerCase());
      setSelectedSupplierInfo(debt || null);
    }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { sparepart_id: '', qty: 1, harga_satuan: 0, keterangan: '' }] }));
  const updItem = (idx, field, value) => {
    const items = [...form.items]; items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };
  const delItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    setError('');
    if (form.items.length === 0 || !form.items[0].sparepart_id) { setError('Minimal 1 item sparepart'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, gudang_id: parseInt(form.gudang_id) || 0,
        items: form.items.map(i => ({ sparepart_id: Number(i.sparepart_id), qty: Number(i.qty), harga_satuan: Number(i.harga_satuan), keterangan: i.keterangan }))
      };
      if (editing) {
        await updatePembelian(editing.id, payload);
      } else {
        await createPembelian(payload);
      }
      setShowModal(false); setEditing(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus pembelian ini?')) return;
    try { await deletePembelian(id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  const handleVerifikasi = async (id, action) => {
    if (!window.confirm(`Yakin ${action === 'approve' ? 'menyetujui' : 'menolak'} pembelian ini?\n\n${action === 'approve' ? 'Stok sparepart akan ditambahkan dan jurnal akuntansi akan dibuat.' : 'Pembelian tidak akan diproses.'}`)) return;
    try {
      await verifikasiPembelian(id, action);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal verifikasi');
    }
  };

  const openModal = () => {
    setError('');
    setEditing(null);
    setSelectedSupplierInfo(null);
    setDiskonMode('persen');      setForm({
      tanggal: new Date().toISOString().slice(0, 10), supplier_name: '', supplier_phone: '',
      supplier_address: '', gudang_id: '', notes: '',
      top: 0, metode_pembayaran: '',
      diskon_persen: 0, diskon_rupiah: 0,
      ppn_aktif: false, ppn_persen: 11,
      biaya_lain: 0, biaya_lain_catatan: '',
      no_faktur: '', nama_petugas: '', catatan_reimbursement: '',
      items: [{ sparepart_id: '', qty: 1, harga_satuan: 0, keterangan: '' }]
    });
    setShowModal(true);
  };

  const openEdit = async (item) => {
    setError('');
    setSaving(false);
    try {
      const res = await getPembelianById(item.id);
      const d = res.data;
      if (!d) { alert('Gagal memuat data pembelian'); return; }

      setEditing(item);
      setSelectedSupplierInfo(null);
      setDiskonMode(d.diskon_persen > 0 ? 'persen' : 'rupiah');
      setForm({
        tanggal: d.tanggal || new Date().toISOString().slice(0, 10),
        supplier_name: d.supplier_name || '',
        supplier_phone: d.supplier_phone || '',
        supplier_address: d.supplier_address || '',
        gudang_id: d.gudang_id || '',
        notes: d.notes || '',
        top: d.top || 0,
        metode_pembayaran: d.metode_pembayaran || '',
        diskon_persen: d.diskon_persen || 0,
        diskon_rupiah: d.diskon_rupiah || 0,
        ppn_aktif: d.ppn_aktif || false,
        ppn_persen: d.ppn_persen || 11,
        biaya_lain: d.biaya_lain || 0,
        biaya_lain_catatan: d.biaya_lain_catatan || '',
        no_faktur: d.no_faktur || '',
        nama_petugas: d.nama_petugas || '',
        catatan_reimbursement: d.catatan_reimbursement || '',
        items: (d.items || []).map(it => ({
          sparepart_id: it.sparepart_id,
          qty: it.qty,
          harga_satuan: it.harga_satuan,
          keterangan: it.keterangan || ''
        }))
      });
      setShowModal(true);
    } catch (err) {
      console.error('Gagal muat data edit', err);
      alert('Gagal memuat data pembelian');
    }
  };

  const sparepartMap = {};
  spareparts.forEach(s => { sparepartMap[s.id] = s; });

  // ===== PDF Cetak Nota Pembelian =====
  const generatePurchasePDF = async (item) => {
    if (!item) return;
    try {
      const res = await getPembelianById(item.id);
      const d = res.data;
      if (!d) return;

      const { jsPDF } = require('jspdf');
      const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentW = pageW - 2 * margin;

      const primary = [79, 110, 247];
      const primaryDark = [59, 90, 227];
      const dark = [23, 43, 77];
      const gray = [136, 152, 170];
      const lightBg = [248, 249, 252];


      const rupiah = (v) => `Rp ${(Number(v) || 0).toLocaleString('id-ID')}`;

      // Helper: section title
      const sectionTitle = (title, yPos) => {
        doc.setFillColor(...primary);
        doc.rect(margin, yPos, 3, 8, 'F');
        doc.setTextColor(...dark);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(title, margin + 7, yPos + 6);
        return yPos + 14;
      };

      // === HEADER ===
      doc.setFillColor(...primary);
      doc.rect(0, 0, pageW, 48, 'F');
      doc.setFillColor(...primaryDark);
      doc.rect(0, 0, pageW, 3, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont('helvetica', 'bold');
      doc.text('NOTA PEMBELIAN LANGSUNG', margin, 22);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`No. Ref: ${d.no_ref || '-'}`, margin, 32);
      doc.text(`Tanggal: ${d.tanggal || '-'}`, margin, 40);

      // Company info on right
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Layanan Synexa', pageW - margin, 18, { align: 'right' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Fleet Management System', pageW - margin, 25, { align: 'right' });
      doc.text('Jl. Contoh No. 123, Jakarta', pageW - margin, 31, { align: 'right' });
      doc.text('Telp: (021) 1234-5678', pageW - margin, 37, { align: 'right' });

      // Status badge
      const statusColor = d.status === 'Diterima' ? [209, 250, 229] : d.status === 'Dibatalkan' ? [254, 226, 226] : [243, 244, 246];
      const statusTextColor = d.status === 'Diterima' ? [6, 95, 70] : d.status === 'Dibatalkan' ? [153, 27, 27] : [55, 65, 81];
      doc.setFillColor(...statusColor);
      doc.roundedRect(pageW - margin - 42, 6, 38, 9, 2, 2, 'F');
      doc.setTextColor(...statusTextColor);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text(d.status || '-', pageW - margin - 23, 12, { align: 'center' });

      // === SUPPLIER & TRANSACTION INFO ===
      let y = sectionTitle('DATA SUPPLIER & TRANSAKSI', 55);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...gray);

      const drawField = (label, value, x, yPos) => {
        doc.setTextColor(...gray);
        doc.setFontSize(7);
        doc.text(label, x, yPos);
        doc.setTextColor(...dark);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text(value || '-', x, yPos + 4);
        doc.setFont('helvetica', 'normal');
        return yPos + 11;
      };

      const leftX = margin;
      const rightX = margin + contentW / 2 + 5;

      let ly = y;
      ly = drawField('Nama Supplier', d.supplier_name || '-', leftX, ly);
      ly = drawField('Alamat', d.supplier_address || '-', leftX, ly);
      if (d.supplier_phone) ly = drawField('Telepon', d.supplier_phone, leftX, ly);

      // Info rekening supplier dari master supplier — supaya admin langsung tahu
      // tujuan transfer saat nota ini dibayar.
      const masterSupplier = (suppliers || []).find(s =>
        String(s.nama || '').trim().toLowerCase() === String(d.supplier_name || '').trim().toLowerCase()
      );
      if (masterSupplier && (masterSupplier.no_rekening || masterSupplier.nama_bank || masterSupplier.nama_rekening)) {
        const bankParts = [];
        if (masterSupplier.nama_bank) bankParts.push(masterSupplier.nama_bank);
        if (masterSupplier.nama_rekening) bankParts.push(`a.n. ${masterSupplier.nama_rekening}`);
        ly = drawField('Rekening Transfer', `${masterSupplier.no_rekening || '-'}${bankParts.length ? ' · ' + bankParts.join(' · ') : ''}`, leftX, ly);
      }

      let ry = y;
      ry = drawField('No. Referensi', d.no_ref || '-', rightX, ry);
      ry = drawField('No. Faktur', d.no_faktur || '-', rightX, ry);
      ry = drawField('Gudang', d.gudang_nama || '-', rightX, ry);
      if (d.top > 0) ry = drawField(`TOP (Jatuh Tempo)`, `${d.top} Hari`, rightX, ry);
      if (d.metode_pembayaran) ry = drawField('Metode Bayar', d.metode_pembayaran, rightX, ry);
      if (d.metode_pembayaran === 'Reimbursement' && d.nama_petugas) ry = drawField('Nama Petugas', d.nama_petugas, rightX, ry);
      if (d.metode_pembayaran === 'Reimbursement' && d.catatan_reimbursement) ry = drawField('Catatan Reimbursement', d.catatan_reimbursement, rightX, ry);

      y = Math.max(ly, ry) + 4;

      // === ITEMS TABLE ===
      y = sectionTitle('DETAIL ITEM PEMBELIAN', y);

      const headers = [['No', 'Kode', 'Nama Sparepart', 'Qty', 'Harga Satuan', 'Subtotal']];
      const rows = (d.items || []).map((it, i) => [
        (i + 1).toString(),
        it.code || '-',
        it.name || '-',
        it.qty.toString(),
        rupiah(it.harga_satuan),
        rupiah(it.subtotal),
      ]);

      // Totals row
      const totalQty = (d.items || []).reduce((s, it) => s + (it.qty || 0), 0);
      const totalSubtotal = (d.items || []).reduce((s, it) => s + (it.subtotal || 0), 0);
      rows.push(['', '', 'TOTAL', totalQty.toString(), '', rupiah(totalSubtotal)]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: headers,
        body: rows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.4, textColor: [50, 50, 50] },
        headStyles: { fillColor: primary, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        columnStyles: {
          0: { halign: 'center', cellWidth: 12 },
          1: { cellWidth: 25, fontStyle: 'bold' },
          3: { halign: 'center', cellWidth: 18 },
          4: { halign: 'right', cellWidth: 35 },
          5: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
        },
        footStyles: { fillColor: lightBg, textColor: dark, fontStyle: 'bold', fontSize: 8 },
      });

      y = doc.lastAutoTable.finalY + 8;

      // === FINANCIAL SUMMARY ===
      const subtotalItems = (d.items || []).reduce((s, it) => s + (it.subtotal || 0), 0);
      const diskon = d.diskon_rupiah > 0 ? d.diskon_rupiah : (subtotalItems * (d.diskon_persen || 0) / 100);

      doc.setFillColor(...lightBg);
      doc.roundedRect(margin, y, contentW, 10 + (d.ppn_aktif ? 5 : 0) + (d.biaya_lain > 0 ? 5 : 0), 3, 3, 'F');

      let summaryY = y + 7;
      doc.setTextColor(...gray);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');

      doc.text('Subtotal Barang', margin + 10, summaryY);
      doc.text(rupiah(subtotalItems), pageW - margin - 12, summaryY, { align: 'right' });
      summaryY += 5;

      if (diskon > 0) {
        doc.text(`Diskon${d.diskon_persen > 0 ? ` (${d.diskon_persen}%)` : ''}`, margin + 10, summaryY);
        doc.text(`- ${rupiah(diskon)}`, pageW - margin - 12, summaryY, { align: 'right' });
        summaryY += 5;
      }

      if (d.ppn_aktif && d.ppn > 0) {
        doc.text(`PPN ${d.ppn_persen || 11}%`, margin + 10, summaryY);
        doc.text(rupiah(d.ppn), pageW - margin - 12, summaryY, { align: 'right' });
        summaryY += 5;
      }

      if (d.biaya_lain > 0) {
        const biayaLabel = d.biaya_lain_catatan ? `Biaya Lain (${d.biaya_lain_catatan})` : 'Biaya Lain';
        doc.text(biayaLabel, margin + 10, summaryY);
        doc.text(rupiah(d.biaya_lain), pageW - margin - 12, summaryY, { align: 'right' });
        summaryY += 5;
      }

      summaryY += 2;

      // Grand Total Box
      doc.setFillColor(...primary);
      doc.roundedRect(margin, summaryY - 4, contentW, 14, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('GRAND TOTAL', margin + 12, summaryY + 6);
      doc.text(rupiah(d.total_amount), pageW - margin - 12, summaryY + 6, { align: 'right' });

      y = summaryY + 16;

      // === NOTES ===
      if (d.notes) {
        y = sectionTitle('CATATAN', y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...gray);
        const noteLines = doc.splitTextToSize(d.notes, contentW);
        doc.text(noteLines, margin, y);
        y += noteLines.length * 4 + 8;
      }

      // === FOOTER ===
      const footerY = pageH - 40;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, footerY, pageW - margin, footerY);

      const now = new Date();
      const dateStr = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text(`Dicetak pada: ${dateStr}`, margin, footerY + 5);
      doc.text(`No. Ref: ${d.no_ref || '-'}`, margin, footerY + 10);
      if (d.metode_pembayaran === 'Reimbursement' && d.nama_petugas) {
        doc.setTextColor(...gray);
        doc.text(`Reimbursement untuk: ${d.nama_petugas}`, margin, footerY + 15);
      }

      // Signature blocks
      const sigWidth = 60;
      const sigLeft = margin;
      const sigRight = pageW - margin - sigWidth;

      doc.setTextColor(...gray);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('Diterima oleh,', sigLeft, footerY - 25);
      doc.text('( ........................... )', sigLeft, footerY - 8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...dark);
      doc.setFontSize(7);
      doc.text(d.supplier_name || 'Supplier', sigLeft, footerY - 2);

      doc.setTextColor(...gray);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text('Mengetahui,', sigRight, footerY - 25);
      doc.text('( ........................... )', sigRight, footerY - 8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...dark);
      doc.setFontSize(7);
      doc.text('Kepala Bagian', sigRight, footerY - 2);

      doc.save(`Nota_Pembelian_${d.no_ref || d.id}.pdf`);
    } catch (err) {
      console.error('Gagal generate PDF', err);
      alert('Gagal mencetak Nota Pembelian');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Pembelian Langsung</h2>
            <p className="page-desc">{data.length} transaksi pembelian langsung (tanpa PO)</p>
          </div>
          {hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={openModal}>
              <Plus size={16} /><span>Buat Pembelian Langsung</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter tanggal — default bulan berjalan */}
      <div className="filters" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Tanggal:</span>
          <input type="date" value={filterTglAwal} onChange={e => setFilterTglAwal(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.8rem' }} />
          <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>s.d</span>
          <input type="date" value={filterTglAkhir} onChange={e => setFilterTglAkhir(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.8rem' }} />
          <button onClick={() => { setFilterTglAwal(getCurrentMonthRange().awal); setFilterTglAkhir(getCurrentMonthRange().akhir); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: '0.75rem', cursor: 'pointer', color: '#475569' }}>Bulan Ini</button>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#64748b' }}>
          {filterTglAwal && filterTglAkhir
            ? `${filteredData.length} dari ${data.length} transaksi (${filterTglAwal} s.d ${filterTglAkhir})`
            : `${data.length} transaksi (semua periode)`}
        </div>
      </div>

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No. Ref</th><th>Tanggal</th><th>Supplier</th><th>Gudang</th>
                <th>Qty</th><th>Total</th><th>Status</th><th>Metode</th><th>Catatan</th>
                <th>Verifikasi</th><th style={{ width: 60 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map(d => (
                <tr key={d.id}>
                  <td><strong style={{ fontSize: '0.8rem' }}>{d.no_ref}</strong></td>
                  <td>{d.tanggal}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{d.supplier_name || '-'}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{d.supplier_phone}</div>
                  </td>
                  <td>{d.gudang_nama || '-'}</td>
                  <td>{d.total_qty}</td>
                  <td style={{ fontWeight: 600 }}>{rupiah(d.total_amount)}</td>
                  <td><span className={`badge ${
                    d.status === 'Diterima' ? 'badge-success' : 
                    d.status === 'Menunggu' ? 'badge-warning' : 
                    d.status === 'Ditolak' ? 'badge-danger' : 
                    d.status === 'Dibatalkan' ? 'badge-danger' : ''
                  }`} style={{
                    background: d.status === 'Menunggu' ? '#fef3c7' : undefined,
                    color: d.status === 'Menunggu' ? '#92400e' : undefined,
                  }}>{d.status}</span></td>
                  <td>
                    <span className={`badge ${d.metode_pembayaran === 'Reimbursement' ? 'badge-warning' : d.metode_pembayaran ? 'badge-default' : ''}`} style={{
                      background: d.metode_pembayaran === 'Reimbursement' ? '#ede9fe' : undefined,
                      color: d.metode_pembayaran === 'Reimbursement' ? '#5b21b6' : undefined,
                      fontWeight: 600,
                    }}>
                      {d.metode_pembayaran || '-'}
                    </span>
                    {d.metode_pembayaran === 'Reimbursement' && d.nama_petugas && (
                      <div style={{ fontSize: '0.72rem', color: '#7c3aed', marginTop: 3 }}>
                        {d.nama_petugas}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: '0.75rem', maxWidth: 200 }}>
                    {d.metode_pembayaran === 'Reimbursement' ? (
                      d.catatan_reimbursement
                        ? <span style={{ color: '#6d28d9', fontWeight: 500 }}>{d.catatan_reimbursement}</span>
                        : <span style={{ color: 'var(--gray-400)' }}>-</span>
                    ) : (
                      <span style={{ color: d.notes ? 'var(--gray-500)' : 'var(--gray-400)' }}>{d.notes || '-'}</span>
                    )}
                  </td>
                  <td style={{ fontSize: '0.75rem' }}>
                    {d.status === 'Ditolak' && d.verified_by ? (
                      <div>
                        <span style={{ fontWeight: 500, color: '#dc2626' }}>{d.verified_by} (Tolak)</span>
                        {d.verified_at && <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>{new Date(d.verified_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
                      </div>
                    ) : d.status !== 'Menunggu' && d.verified_by ? (
                      <div>
                        <span style={{ fontWeight: 500, color: '#374151' }}>{d.verified_by}</span>
                        {d.verified_at && <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>{new Date(d.verified_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
                      </div>
                    ) : (
                      <span style={{ color: '#d1d5db' }}>-</span>
                    )}
                  </td>
                  <td>
                    {d.status === 'Menunggu' && hasMenuAction('inventori', 'edit') && (
                      <>
                        <button className="btn-icon" title="Setujui Pembelian" onClick={() => handleVerifikasi(d.id, 'approve')} style={{ color: '#059669' }}><CheckCircle size={14} /></button>
                        <button className="btn-icon" title="Tolak Pembelian" onClick={() => handleVerifikasi(d.id, 'reject')} style={{ color: '#dc2626' }}><XCircle size={14} /></button>
                      </>
                    )}
                    {hasMenuAction('inventori', 'edit') && d.status === 'Menunggu' && (
                      <button className="btn-icon" title="Edit Pembelian" onClick={() => openEdit(d)} style={{ color: '#fb6340' }}><Edit2 size={14} /></button>
                    )}
                    <button className="btn-icon" title="Cetak Nota Pembelian" onClick={() => generatePurchasePDF(d)} style={{ color: '#11cdef' }}><Printer size={14} /></button>
                {hasMenuAction('inventori', 'delete') && (
                      <button className="btn-icon btn-danger" onClick={() => handleDelete(d.id)}><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={11} className="text-center text-muted" style={{ padding: 40 }}>Belum ada pembelian langsung</td></tr>}
              {data.length > 0 && filteredData.length === 0 && <tr><td colSpan={11} className="text-center text-muted" style={{ padding: 40 }}>Tidak ada data pada periode {filterTglAwal} s.d {filterTglAkhir}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* PO Picker Dialog */}
      {showPoPicker && (
        <div className="modal-overlay" onClick={() => setShowPoPicker(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
            <div className="modal-header">
              <h3><ShoppingCart size={18} style={{ marginRight: 8 }} />Pilih Purchase Order</h3>
              <button className="btn-icon" onClick={() => setShowPoPicker(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-400)', marginBottom: 12 }}>
                Pilih Purchase Order untuk ditarik datanya ke form Pembelian. Data supplier dan item akan terisi otomatis.
              </p>
              <div className="table-wrapper" style={{ boxShadow: 'none', border: '1px solid var(--gray-200)' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>No. PO</th><th>Tanggal</th><th>Supplier</th><th>Item</th><th>Total</th><th>Status</th><th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {poList.filter(p => p.status !== 'Dibatalkan').map(p => (
                      <tr key={p.id}>
                        <td><strong style={{ fontSize: '0.8rem' }}>{p.no_po}</strong></td>
                        <td>{p.tanggal}</td>
                        <td>{p.supplier_name || '-'}</td>
                        <td>{p.total_qty}</td>
                        <td style={{ fontWeight: 600 }}>{rupiah(p.total_amount)}</td>
                        <td><span className={`badge ${p.status === 'Selesai' ? 'badge-success' : p.status === 'Dikirim' ? 'badge-warning' : 'badge-default'}`}>{p.status}</span></td>
                        <td>
                          <button className="btn btn-primary" onClick={() => handleTarikPO(p.id)} style={{ padding: '6px 14px', fontSize: '0.78rem' }}>
                            <ShoppingCart size={14} /> Pilih
                          </button>
                        </td>
                      </tr>
                    ))}
                    {poList.filter(p => p.status !== 'Dibatalkan').length === 0 && (
                      <tr><td colSpan={7} className="text-center text-muted" style={{ padding: 40 }}>Belum ada Purchase Order yang tersedia</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPoPicker(false)}>Tutup</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="modal-header">
              <h3><Package size={18} style={{ marginRight: 8 }} />{editing ? 'Edit Pembelian Langsung' : 'Buat Pembelian Langsung Baru'}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {!editing && (
                  <button className="btn btn-secondary" onClick={() => setShowPoPicker(true)} style={{ padding: '6px 12px', fontSize: '0.78rem' }}>
                    <ShoppingCart size={14} /> Tarik dari PO
                  </button>
                )}
                <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
              </div>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}

              {/* === SUPPLIER INFO SECTION === */}
              <div style={{ background: '#f0f4ff', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #d6e4ff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Building2 size={16} style={{ color: '#4f6ef7' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#2c3e6b', textTransform: 'uppercase', letterSpacing: 0.3 }}>Informasi Supplier</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#5a6f9c', marginBottom: 4, display: 'block' }}>Supplier</label>
                    <select
                      value={suppliers.find(s => s.nama === form.supplier_name)?.id || ''}
                      onChange={e => handleSupplierSelect(e.target.value)}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 6,
                        border: '1px solid #d6e4ff', fontSize: '0.85rem', background: 'white'
                      }}
                    >
                      <option value="">-- Pilih Supplier --</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.kode ? `[${s.kode}] ` : ''}{s.nama}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#5a6f9c', marginBottom: 4, display: 'block' }}>Telepon</label>
                    <input
                      type="text"
                      value={form.supplier_phone}
                      onChange={e => setForm({ ...form, supplier_phone: e.target.value })}
                      placeholder="Nomor telepon supplier"
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d6e4ff', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <label style={{ fontSize: '0.75rem', fontWeight: 500, color: '#5a6f9c', marginBottom: 4, display: 'block' }}>Alamat</label>
                  <input
                    type="text"
                    value={form.supplier_address}
                    onChange={e => setForm({ ...form, supplier_address: e.target.value })}
                    placeholder="Alamat supplier"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #d6e4ff', fontSize: '0.85rem' }}
                  />
                </div>

                {/* Supplier Debt Info Card */}
                {selectedSupplierInfo && (
                  <div style={{
                    marginTop: 10, padding: '10px 14px', borderRadius: 8,
                    background: 'linear-gradient(135deg, #fff3e0, #fff8e6)',
                    border: '1px solid #ffe0b2',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DollarSign size={18} style={{ color: '#e65100' }} />
                      <div>
                        <span style={{ fontSize: '0.78rem', color: '#bf5f00' }}>Sisa Hutang Supplier</span>
                        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#e65100' }}>
                          {rupiah(selectedSupplierInfo.sisa_hutang)}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#8d6e00', textAlign: 'right' }}>
                      <div style={{ fontWeight: 500 }}>{selectedSupplierInfo.supplier_name}</div>
                      <div>(dari transaksi sebelumnya)</div>
                    </div>
                  </div>
                )}
              </div>

              {/* === TRANSACTION INFO === */}
              <div style={{
                background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 16,
                border: '1px solid #e2e8f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <Receipt size={16} style={{ color: '#475569' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3 }}>Detail Transaksi</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <div className="form-group"><label>Tanggal</label><input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} /></div>
                  <div className="form-group"><label><FileText size={13} style={{ marginRight: 4 }} />No. Faktur</label><input type="text" value={form.no_faktur} onChange={e => setForm({ ...form, no_faktur: e.target.value })} placeholder="Faktur-001" /></div>
                  <div className="form-group"><label><Clock size={13} style={{ marginRight: 4 }} />TOP (Jatuh Tempo)</label>
                    <select value={form.top} onChange={e => setForm({ ...form, top: parseInt(e.target.value) || 0 })}>
                      <option value="0">Kontan</option><option value="7">7 Hari</option><option value="14">14 Hari</option>
                      <option value="30">30 Hari</option><option value="45">45 Hari</option><option value="60">60 Hari</option>
                    </select>
                  </div>
                  <div className="form-group"><label><CreditCard size={13} style={{ marginRight: 4 }} />Metode Pembayaran</label>
                    <select value={form.metode_pembayaran} onChange={e => setForm({ ...form, metode_pembayaran: e.target.value })}>
                      <option value="">-- Pilih --</option>
                      <option value="Tunai">Tunai</option><option value="Transfer">Transfer</option>
                      <option value="Cek/Giro">Cek / Giro</option>
                      <option value="Reimbursement">Reimbursement (Raimbersmen)</option>
                      <option value="Petty Cash">Petty Cash (Kas Kecil)</option>
                      <option value="Lainnya">Lainnya</option>
                    </select>
                  </div>
                  {form.metode_pembayaran === 'Reimbursement' && (
                    <>
                      <div className="form-group"><label><User size={13} style={{ marginRight: 4 }} />Nama Petugas / Teknisi</label>
                        <input
                          type="text"
                          value={form.nama_petugas}
                          onChange={e => setForm({ ...form, nama_petugas: e.target.value })}
                          placeholder="Nama petugas/teknisi yang membayar dengan dana pribadi"
                        />
                      </div>
                      <div className="form-group"><label><FileText size={13} style={{ marginRight: 4 }} />Catatan Reimbursement</label>
                        <input
                          type="text"
                          value={form.catatan_reimbursement}
                          onChange={e => setForm({ ...form, catatan_reimbursement: e.target.value })}
                          placeholder="Mis. ditalangi untuk beli bearing mendesak"
                        />
                      </div>
                    </>
                  )}
                  <div className="form-group"><label>Gudang</label>
                    <select value={form.gudang_id} onChange={e => setForm({ ...form, gudang_id: e.target.value })}>
                      <option value="">-- Pilih Gudang --</option>
                      {gudangList.map(g => <option key={g.id} value={g.id}>{g.nama}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label>Catatan</label><input type="text" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Catatan (opsional)" /></div>
                </div>
              </div>

              {/* === ITEMS TABLE === */}
              <div style={{
                background: '#fafbfc', borderRadius: 10, border: '1px solid #e8ecf0',
                padding: '12px 14px', marginBottom: 12
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Package size={15} style={{ color: '#475569' }} />
                    <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      Item Pembelian
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', background: '#eef2f6', padding: '1px 8px', borderRadius: 10 }}>
                      {form.items.length} item
                    </span>
                  </div>
                </div>

                {/* Table Header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '40px 2fr 70px 110px 120px 32px',
                  gap: 6, padding: '6px 8px', fontSize: '0.7rem', fontWeight: 600,
                  color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3,
                  background: '#eef2f6', borderRadius: 6, marginBottom: 6
                }}>
                  <div>No</div>
                  <div>Sparepart</div>
                  <div style={{ textAlign: 'right' }}>Qty</div>
                  <div style={{ textAlign: 'right' }}>Harga Satuan</div>
                  <div style={{ textAlign: 'right' }}>Subtotal</div>
                  <div></div>
                </div>

                {/* Table Rows */}
                {form.items.map((item, idx) => {
                  const sp = sparepartMap[item.sparepart_id];
                  const subtotal = (Number(item.qty) || 0) * (Number(item.harga_satuan) || 0);
                  return (
                    <div key={idx} style={{
                      display: 'grid', gridTemplateColumns: '40px 2fr 70px 110px 120px 32px',
                      gap: 6, padding: '5px 8px', alignItems: 'center',
                      borderBottom: idx < form.items.length - 1 ? '1px solid #eef2f6' : 'none',
                      background: idx % 2 === 0 ? 'white' : '#f8fafc',
                      borderRadius: idx % 2 === 0 ? 0 : 4
                    }}>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>{idx + 1}</div>
                      <div>
                        <SearchableSelect
                          options={spareparts.map(s => ({ id: s.id, code: s.code || '', name: s.name, stock: s.stock }))}
                          value={item.sparepart_id}
                          onChange={spId => {
                            const s = sparepartMap[spId];
                            const items = [...form.items];
                            items[idx] = { ...items[idx], sparepart_id: spId, harga_satuan: s ? Number(s.harga_beli || s.price || 0) : 0 };
                            setForm({ ...form, items });
                          }}
                          placeholder="Ketik kode / nama item..."
                        />
                        {sp && (
                          <div style={{ fontSize: '0.68rem', color: '#6b7280', marginTop: 2 }}>
                            Stok: {sp.stock} {sp.unit || ''} | Harga beli: {rupiah(sp.harga_beli || sp.price || 0)}
                          </div>
                        )}
                      </div>
                      <div>
                        <input
                          type="number"
                          value={item.qty}
                          onChange={e => updItem(idx, 'qty', e.target.value)}
                          min="1"
                          style={{
                            width: '100%', padding: '6px 6px', borderRadius: 5,
                            border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right'
                          }}
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          value={item.harga_satuan}
                          onChange={e => updItem(idx, 'harga_satuan', e.target.value)}
                          min="0"
                          style={{
                            width: '100%', padding: '6px 6px', borderRadius: 5,
                            border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right'
                          }}
                        />
                      </div>
                      <div style={{ textAlign: 'right', fontWeight: 600, fontSize: '0.82rem', color: '#0369a1', padding: '0 4px' }}>
                        {rupiah(subtotal)}
                      </div>
                      <div>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => delItem(idx)}
                          disabled={form.items.length === 1}
                          style={{ opacity: form.items.length === 1 ? 0.3 : 1 }}
                        >
                          <X size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Add Item Button */}
                <button
                  onClick={addItem}
                  style={{
                    marginTop: 10, padding: '7px 14px', fontSize: '0.8rem',
                    background: 'white', border: '1px dashed #94a3b8',
                    borderRadius: 6, cursor: 'pointer', color: '#475569',
                    display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                    justifyContent: 'center', transition: 'all 0.15s'
                  }}
                  onMouseEnter={e => { e.target.style.background = '#f1f5f9'; e.target.style.borderColor = '#64748b'; }}
                  onMouseLeave={e => { e.target.style.background = 'white'; e.target.style.borderColor = '#94a3b8'; }}
                >
                  <Plus size={14} /> Tambah Item
                </button>
              </div>

              {/* === FINANCIAL DETAILS === */}
              <div style={{
                background: '#fafbfc', borderRadius: 10, border: '1px solid #e8ecf0',
                padding: '12px 14px', marginBottom: 12
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <DollarSign size={15} style={{ color: '#475569' }} />
                  <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#334155', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    Diskon, PPN & Biaya Lain
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {/* Diskon */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                      <label><Percent size={13} style={{ marginRight: 4 }} />Diskon</label>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => setDiskonMode('persen')}
                          style={{
                            padding: '6px 10px', fontSize: '0.75rem', borderRadius: 5,
                            border: `1px solid ${diskonMode === 'persen' ? '#4f6ef7' : '#d1d5db'}`,
                            background: diskonMode === 'persen' ? '#eef2ff' : 'white',
                            color: diskonMode === 'persen' ? '#4f6ef7' : '#64748b',
                            cursor: 'pointer', fontWeight: 600
                          }}
                        >%</button>
                        <button
                          onClick={() => setDiskonMode('rupiah')}
                          style={{
                            padding: '6px 10px', fontSize: '0.75rem', borderRadius: 5,
                            border: `1px solid ${diskonMode === 'rupiah' ? '#4f6ef7' : '#d1d5db'}`,
                            background: diskonMode === 'rupiah' ? '#eef2ff' : 'white',
                            color: diskonMode === 'rupiah' ? '#4f6ef7' : '#64748b',
                            cursor: 'pointer', fontWeight: 600
                          }}
                        >Rp</button>
                        <input
                          type="number"
                          value={diskonMode === 'persen' ? form.diskon_persen : form.diskon_rupiah}
                          onChange={e => {
                            const v = Number(e.target.value) || 0;
                            if (diskonMode === 'persen') setForm({ ...form, diskon_persen: v });
                            else setForm({ ...form, diskon_rupiah: v });
                          }}
                          min="0"
                          style={{ flex: 1, padding: '6px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* PPN */}
                  <div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                      <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>PPN</label>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <label style={{
                            padding: '6px 12px', fontSize: '0.75rem', borderRadius: 5,
                            border: `1px solid ${form.ppn_aktif ? '#059669' : '#d1d5db'}`,
                            background: form.ppn_aktif ? '#ecfdf5' : 'white',
                            color: form.ppn_aktif ? '#059669' : '#64748b',
                            cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4
                          }}>
                            <input
                              type="checkbox"
                              checked={form.ppn_aktif}
                              onChange={e => setForm({ ...form, ppn_aktif: e.target.checked })}
                              style={{ width: 14, height: 14 }}
                            />
                            Aktif
                          </label>
                          {form.ppn_aktif && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="number"
                                value={form.ppn_persen}
                                onChange={e => setForm({ ...form, ppn_persen: Number(e.target.value) || 0 })}
                                min="0" max="100"
                                style={{ width: 60, padding: '6px 6px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }}
                              />
                              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>%</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Biaya Lain & No Faktur */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Biaya Lain-lain</label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type="number"
                        value={form.biaya_lain}
                        onChange={e => setForm({ ...form, biaya_lain: Number(e.target.value) || 0 })}
                        min="0"
                        placeholder="0"
                        style={{ width: 120, padding: '6px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem', textAlign: 'right' }}
                      />
                      <input
                        type="text"
                        value={form.biaya_lain_catatan}
                        onChange={e => setForm({ ...form, biaya_lain_catatan: e.target.value })}
                        placeholder="mis: biaya kirim, materai"
                        style={{ flex: 1, padding: '6px 8px', borderRadius: 5, border: '1px solid #d1d5db', fontSize: '0.8rem' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* === SUMMARY === */}
              <div style={{
                background: 'linear-gradient(135deg, #f0f9ff, #e6f7ff)',
                borderRadius: 10, padding: '12px 18px',
                border: '1px solid #bae6fd',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 8 }}>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Total Item</span>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#075985' }}>{form.items.length}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Total Qty</span>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#075985' }}>{totalQty}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>Subtotal Barang</span>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#075985' }}>{rupiah(subtotalItems)}</div>
                  </div>
                </div>
                <div style={{ borderTop: '1px dashed #bae6fd', paddingTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#475569', marginBottom: 4 }}>
                    <span>DPP (Subtotal - Diskon)</span>
                    <span style={{ fontWeight: 600, color: '#0369a1' }}>{rupiah(dpp)}</span>
                  </div>
                  {form.ppn_aktif && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#475569', marginBottom: 4 }}>
                      <span>PPN {form.ppn_persen}%</span>
                      <span style={{ fontWeight: 600, color: '#0369a1' }}>{rupiah(ppn)}</span>
                    </div>
                  )}
                  {biayaLain > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', color: '#475569', marginBottom: 4 }}>
                      <span>Biaya Lain {form.biaya_lain_catatan ? `(${form.biaya_lain_catatan})` : ''}</span>
                      <span style={{ fontWeight: 600, color: '#0369a1' }}>{rupiah(biayaLain)}</span>
                    </div>
                  )}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    borderTop: '1px solid #bae6fd', paddingTop: 8, marginTop: 4
                  }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#075985' }}>Grand Total</span>
                    <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0369a1' }}>{rupiah(grandTotal)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Menyimpan...' : editing ? <><Package size={16} /> Simpan Perubahan</> : <><Truck size={16} /> Proses Pembelian Langsung</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
